import { Select } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getSteps } from '../../../api/courses.api';
import { updateLevelUpResult } from '../../../api/semesters.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import { ILevelUpStudent } from '../../../models/semesters.model';
import { RootState } from '../../../store/rootReducer';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import LevelUpTableHeader from '../../program/semester/modals/LevelUpTableHeader';

interface IProps {
  data: ILevelUpStudent[] | undefined;
  doGetLevelUpStudents: () => void;
}

const LevelUpTable: FC<IProps> = ({ data, doGetLevelUpStudents }) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const { successNotification } = useNotification();
  // GR0005 = 레벨업 결과
  const GR0005 = commonCodes.find(
    (item) => item.commonCode === 'GR0005',
  )?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<any>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [options, setOptions] = useState<any>([]);

  useEffect(() => {
    execute(getSteps());
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      const _options: any = [];
      res?.data?.forEach((item: any) => {
        const option = (
          <Select.OptGroup label={item.courseName}>
            {item.step.map((subitem: any) => (
              <Select.Option
                key={subitem.courseCode}
                value={String(subitem.courseCode)}
              >
                {subitem.courseName}
              </Select.Option>
            ))}
          </Select.OptGroup>
        );
        _options.push(option);
      });

      setOptions(_options);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetLevelUpStudents();
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  const handleScore = (score: number | null) => {
    if (score === null) {
      return '';
    }
    return Math.round(score);
  };

  const onUpdateLevelUpResult = (
    item: ILevelUpStudent,
    key: string,
    value: string,
  ) => {
    const req: any = {
      levelUpCode: item.levelupCode,
      memberCode: item.memberCode,
      campusLevelupResultCode: item.campusLevelUpResultCode,
      nextCourseCode: item.nextCourseCode,
    };
    req[key] = value;

    submitExecute(updateLevelUpResult(req));
  };

  return (
    <>
      {isLoading && submitIsLoading && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <table>
        <LevelUpTableHeader />
        <tbody>
          {data?.map((item, index) => (
            <>
              <tr key={index}>
                <td className="align-middle" rowSpan={2}>
                  {item.campusKorName}
                </td>
                <td className="align-middle" rowSpan={2}>
                  {item.memberKorName}
                </td>
                <td className="align-middle" rowSpan={2}>
                  {item.className}
                </td>
                <td>{item.monthlyTestScore1}</td>
                <td>{item.monthlyTestScore2}</td>
                <td>{item.levelTestScore}</td>
                <td>{handleScore(item.icoachingAvgScore)}</td>
                <td>{handleScore(item.elearningAvgScore)}</td>
                <td>{item?.totalTestScore?.toFixed(1) || ''}</td>
                <td className="align-middle" rowSpan={2}>{item?.studentRegisterCount}</td>
                <td className="align-middle" rowSpan={2}>
                  {getCommonCodeLabel('GR0005', item.levelUpResultCode, 'ko')}
                </td>
                <td className="align-middle" rowSpan={2}>
                  <Select
                    className="w-100"
                    placeholder={t('class.levelUp.placeholder2')}
                    value={item.campusLevelUpResultCode}
                    onChange={(value) =>
                      onUpdateLevelUpResult(
                        item,
                        'campusLevelupResultCode',
                        value,
                      )
                    }
                  >
                    {GR0005?.map((item) => (
                      <Select.Option
                        key={item.detailCode}
                        value={item.detailCode}
                      >
                        {item.korName}
                      </Select.Option>
                    ))}
                  </Select>
                </td>
                <td className="align-middle" rowSpan={2}>
                  <Select
                    className="w-100"
                    placeholder={t('class.levelUp.placeholder1')}
                    value={String(item.nextCourseCode)}
                    onChange={(value) =>
                      onUpdateLevelUpResult(item, 'nextCourseCode', value)
                    }
                  >
                    {options}
                  </Select>
                </td>
              </tr>
              <tr>
                <td>{item.monthlyTestConversionScore1}</td>
                <td>{item.monthlyTestConversionScore2}</td>
                <td>{item.levelTestConversionScore}</td>
                <td>{handleScore(item.icoachingConversionScore)}</td>
                <td>{handleScore(item.elearningConversionScore)}</td>
                <td>{item?.totalConversionScore?.toFixed(1) || ''}</td>
              </tr>
            </>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default LevelUpTable;
